import {
  ApprovalPolicy,
  ApprovalStatePolicy,
  DocumentApprovalState,
} from '@/types/approvals'
import { orderedUserIds } from '@/utils/approver-entries'

function withPolicies(
  state: DocumentApprovalState,
  policies: ApprovalStatePolicy[],
): DocumentApprovalState {
  return {
    ...state,
    policies,
    is_rejected: policies.some((policy) => policy.is_rejected),
    all_policies_satisfied: policies.every((policy) => policy.satisfied),
  }
}

/**
 * Copies what the server sends back for a policy that was just attached: its
 * current version, no pin, and nobody has approved or rejected yet.
 *
 * Only a policy whose approvers are all named users can be copied this way —
 * see `attachPolicyToState`.
 */
export function freshStatePolicy(policy: ApprovalPolicy): ApprovalStatePolicy {
  const approvers = orderedUserIds(policy.approvers).map(
    (user_id, sort_order) =>
      ({ sort_order, kind: 'user', user_id, latest_action: null } as const),
  )
  const first = approvers[0]

  return {
    approval_policy_id: policy.id,
    description: policy.description,
    approval_policy_version_id: policy.current_version_id,
    pinned_approval_policy_version_id: null,
    approval_order: policy.approval_order,
    approvals_required: policy.approvals_required,
    config_resolved: true,
    approvals_received: 0,
    satisfied: false,
    is_rejected: false,
    next_required_user_id:
      policy.approval_order === 'sequential' ? first?.user_id ?? null : null,
    next_required:
      policy.approval_order === 'sequential' && first
        ? { kind: 'user', sort_order: first.sort_order, user_id: first.user_id }
        : null,
    approvers,
  }
}

/**
 * Adds a policy as if the attach had already landed. Hands back the same
 * state object when there's nothing to add — no policy to build the entry
 * from, it's already attached, or it names a position.
 *
 * A position is skipped and left for the server: only pd-approval knows who
 * currently holds it, so this can't build a correct approver list for one the
 * way it can for a named user. It waits for the refetch instead of guessing
 * and showing the wrong approvers.
 */
export function attachPolicyToState(
  state: DocumentApprovalState,
  policy?: ApprovalPolicy,
): DocumentApprovalState {
  if (!policy) return state
  if (policy.approvers.some(({ kind }) => kind === 'position')) return state
  const alreadyAttached = state.policies.some(
    (statePolicy) => statePolicy.approval_policy_id === policy.id,
  )
  if (alreadyAttached) return state

  return withPolicies(state, [...state.policies, freshStatePolicy(policy)])
}

export function detachPolicyFromState(
  state: DocumentApprovalState,
  policyId: string,
): DocumentApprovalState {
  const policies = state.policies.filter(
    (policy) => policy.approval_policy_id !== policyId,
  )
  if (policies.length === state.policies.length) return state

  return withPolicies(state, policies)
}
